import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../utils/cn';
import { useTheme } from '../theme/useTheme';
const DEFAULT_LABELS = {
    de: { placeholder: 'Suchen oder Befehl eingeben…', empty: 'Keine Treffer.', searching: 'Suche läuft…' },
    en: { placeholder: 'Search or type a command…', empty: 'No results.', searching: 'Searching…' },
};
function matches(item, q) {
    if (!q)
        return true;
    const hay = [item.label, item.group, ...(item.keywords ?? [])].join(' ').toLowerCase();
    return hay.includes(q);
}
/**
 * Globale Command-Palette (Saeule C): ein Ctrl+K fuer Navigation, Aktionen und
 * (optional) Volltext-Suche. Statische Commands werden lokal gefiltert,
 * zusaetzliche Treffer kommen ueber den optionalen `search`-Adapter. Tastatur:
 * Pfeile bewegen, Enter fuehrt aus, Escape schliesst.
 */
export function CommandPalette({ open, onClose, commands, search, searchDebounceMs = 200, labels, className, }) {
    const { language } = useTheme();
    const l = { ...DEFAULT_LABELS[language], ...labels };
    const [query, setQuery] = useState('');
    const [remote, setRemote] = useState([]);
    const [searching, setSearching] = useState(false);
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    // Beim Oeffnen zuruecksetzen und Fokus auf das Eingabefeld.
    useEffect(() => {
        if (open) {
            setQuery('');
            setRemote([]);
            setActive(0);
            const id = window.setTimeout(() => inputRef.current?.focus(), 0);
            return () => window.clearTimeout(id);
        }
    }, [open]);
    // Debounced Remote-Suche mit Race-Schutz.
    useEffect(() => {
        if (!open || !search)
            return;
        const q = query.trim();
        if (!q) {
            setRemote([]);
            setSearching(false);
            return;
        }
        let cancelled = false;
        setSearching(true);
        const handle = window.setTimeout(() => {
            search(q)
                .then((res) => {
                if (!cancelled)
                    setRemote(res);
            })
                .catch(() => {
                if (!cancelled)
                    setRemote([]);
            })
                .finally(() => {
                if (!cancelled)
                    setSearching(false);
            });
        }, searchDebounceMs);
        return () => {
            cancelled = true;
            window.clearTimeout(handle);
        };
    }, [query, open, search, searchDebounceMs]);
    const q = query.trim().toLowerCase();
    const items = useMemo(() => {
        const staticFiltered = commands.filter((c) => matches(c, q));
        const seen = new Set(staticFiltered.map((c) => c.id));
        const merged = [...staticFiltered];
        for (const r of remote) {
            if (!seen.has(r.id)) {
                merged.push(r);
                seen.add(r.id);
            }
        }
        return merged;
    }, [commands, remote, q]);
    // activeIndex in gueltigen Bereich klemmen.
    useEffect(() => {
        setActive((i) => (items.length === 0 ? 0 : Math.min(i, items.length - 1)));
    }, [items.length]);
    if (!open)
        return null;
    const run = (item) => {
        item.perform();
        onClose();
    };
    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => (items.length ? (i + 1) % items.length : 0));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            const item = items[active];
            if (item)
                run(item);
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    };
    const listboxId = 'sec-cmdk-listbox';
    const optionId = (i) => `sec-cmdk-opt-${i}`;
    return (_jsx("div", { className: cn('sec-cmdk', className), onClick: (e) => {
            if (e.target === e.currentTarget)
                onClose();
        }, children: _jsxs("div", { className: "sec-cmdk__panel", role: "dialog", "aria-modal": "true", "aria-label": l.placeholder, children: [_jsx("input", { ref: inputRef, className: "sec-cmdk__input", type: "text", role: "combobox", "aria-expanded": "true", "aria-controls": listboxId, "aria-activedescendant": items[active] ? optionId(active) : undefined, "aria-label": l.placeholder, placeholder: l.placeholder, value: query, onChange: (e) => setQuery(e.target.value), onKeyDown: onKeyDown }), _jsx("div", { id: listboxId, role: "listbox", "aria-label": l.placeholder, className: "sec-cmdk__list", children: items.length === 0 ? (_jsx("div", { role: "option", "aria-disabled": "true", "aria-selected": "false", className: "sec-muted sec-cmdk__empty", "aria-live": "polite", children: searching ? l.searching : l.empty })) : (items.map((item, i) => {
                        const isActive = i === active;
                        const prevGroup = i > 0 ? items[i - 1].group : undefined;
                        const showGroup = item.group && item.group !== prevGroup;
                        return (_jsxs(Fragment, { children: [showGroup ? (_jsx("div", { role: "presentation", className: "sec-cmdk__group", children: item.group })) : null, _jsxs("div", { id: optionId(i), role: "option", "aria-selected": isActive, onMouseEnter: () => setActive(i), onClick: () => run(item), className: cn('sec-cmdk__option', isActive && 'sec-cmdk__option--active'), children: [item.icon ? _jsx("span", { "aria-hidden": "true", className: "sec-cmdk__icon", children: item.icon }) : null, _jsx("span", { className: "sec-cmdk__label", children: item.label }), item.hint ? _jsx("span", { className: "sec-cmdk__hint", children: item.hint }) : null] })] }, item.id));
                    })) })] }) }));
}
